/**
 * Conflict resolution store - tracks pending optimistic mutations, deferred
 * remote changes, and conflict notifications for the UI.
 *
 * When a local mutation (create/update/delete) is in flight and a remote
 * WebSocket change arrives for the same entity, the remote change is deferred
 * until the API call completes. If the remote version has a newer updated_at,
 * it wins (last-write-wins) and a conflict notification is added so the
 * ConflictToast component can inform the user.
 */

import { create } from 'zustand';
import type { EntityType } from '@anna-calendar/shared';

/** A conflict notification for the UI. */
export interface ConflictNotice {
  id: string;
  entityType: EntityType;
  entityTitle: string;
  /** ISO timestamp when the conflict was detected */
  detectedAt: string;
}

interface ConflictsState {
  /** Entity IDs with an API call currently in flight */
  pendingMutations: Set<string>;
  /** Remote changes deferred because a local mutation was in flight */
  deferredChanges: Map<string, { entity: EntityType; action: string; data: Record<string, unknown>; timestamp: string }>;
  /** Conflict notifications to display in the UI */
  conflicts: ConflictNotice[];

  addPending: (id: string) => void;
  removePending: (id: string) => void;
  isPending: (id: string) => boolean;

  deferChange: (entityId: string, entry: { entity: EntityType; action: string; data: Record<string, unknown>; timestamp: string }) => void;
  popDeferred: (entityId: string) => { entity: EntityType; action: string; data: Record<string, unknown>; timestamp: string } | undefined;

  addConflict: (notice: Omit<ConflictNotice, 'id' | 'detectedAt'>) => void;
  dismissConflict: (id: string) => void;
}

export const useConflictsStore = create<ConflictsState>((set, get) => ({
  pendingMutations: new Set(),
  deferredChanges: new Map(),
  conflicts: [],

  addPending: (id) => {
    set((s) => {
      const next = new Set(s.pendingMutations);
      next.add(id);
      return { pendingMutations: next };
    });
  },

  removePending: (id) => {
    set((s) => {
      const next = new Set(s.pendingMutations);
      next.delete(id);
      return { pendingMutations: next };
    });
  },

  isPending: (id) => get().pendingMutations.has(id),

  deferChange: (entityId, entry) => {
    set((s) => {
      const next = new Map(s.deferredChanges);
      next.set(entityId, entry);
      return { deferredChanges: next };
    });
  },

  popDeferred: (entityId) => {
    const deferred = get().deferredChanges.get(entityId);
    if (deferred) {
      set((s) => {
        const next = new Map(s.deferredChanges);
        next.delete(entityId);
        return { deferredChanges: next };
      });
    }
    return deferred;
  },

  addConflict: (notice) => {
    const id = crypto.randomUUID();
    set((s) => ({
      conflicts: [...s.conflicts, { ...notice, id, detectedAt: new Date().toISOString() }],
    }));
  },

  dismissConflict: (id) => {
    set((s) => ({
      conflicts: s.conflicts.filter((c) => c.id !== id),
    }));
  },
}));
